import React, { useState, useRef } from 'react';
import { Input } from 'antd';
import './style.scss';

interface Props {
  length: number;
  onChange: (value: string) => void;
}

interface InputChange {
  onChange: (value: string, inputIndex: number) => void;
}

const renterInput = (
  key: number,
  { onChange }: InputChange,
  value?: string,
  inputRef?: React.RefObject<any>,
): React.ReactElement => {
  const handleInputChange = (val: string) => {
    val.length && focusNextInput(key);
    onChange(val, key);
  };

  return (
    <Input
      key={key}
      ref={inputRef}
      value={value}
      size="large"
      maxLength={1}
      className="verification-input__input"
      onFocus={event => {
        if (event.target.value) {
          event.target.select();
        }
      }}
      onKeyDown={event => {
        const e = event as React.KeyboardEvent<HTMLInputElement>;
        if (e.key === 'Backspace') {
          const target = e.currentTarget;
          const hasSelection = target.selectionStart !== target.selectionEnd;
          // if current is empty or caret at start (and no text is selected), move to previous
          if (!hasSelection && (target.value === '' || target.selectionStart === 0)) {
            const elements = document.getElementsByClassName(
              'verification-input__input',
            ) as HTMLCollection;
            const prevElement = elements[key - 1] as HTMLInputElement | undefined;
            if (prevElement !== undefined) {
              // clear previous and focus it
              onChange('', key - 1);
              prevElement.focus();
              e.preventDefault();
            }
          }
        }
        if (e.key === 'ArrowLeft') {
          const elements = document.getElementsByClassName(
            'verification-input__input',
          ) as HTMLCollection;
          const prevElement = elements[key - 1] as HTMLInputElement | undefined;
          if (prevElement !== undefined) {
            prevElement.focus();
            e.preventDefault();
          }
        }
        if (e.key === 'ArrowRight') {
          const elements = document.getElementsByClassName(
            'verification-input__input',
          ) as HTMLCollection;
          const nextElement = elements[key + 1] as HTMLInputElement | undefined;
          if (nextElement !== undefined) {
            nextElement.focus();
            e.preventDefault();
          }
        }
      }}
      onKeyPress={event => {
        if (!/[0-9]/.test(event.key)) {
          event.preventDefault();
        }
      }}
      onChange={({ target: { value } }) => handleInputChange(value)}
    />
  );
};

const renderElements = (
  length: number,
  { onChange }: InputChange,
  values: string[],
  refs: React.RefObject<any>[],
): React.ReactElement[] => {
  return Array.from<number>({ length }).map((_, index) => {
    return renterInput(index, { onChange }, values[index], refs[index]);
  });
};

const focusNextInput = (currentIndex: number) => {
  const elements = document.getElementsByClassName(
    'verification-input__input',
  ) as HTMLCollection;
  const nextElement = elements[currentIndex + 1] as HTMLInputElement;
  if (nextElement !== undefined) {
    nextElement.focus();
  }
};

export function VerificationInput({
  length,
  onChange,
}: Props): React.ReactElement {
  const [value, setValue] = useState<string[]>(Array.from({ length }).map(() => ''));
  const refs = useRef<React.RefObject<any>[]>(
    Array.from({ length }).map(() => React.createRef<any>()),
  ).current;

  const setAt = (index: number, val: string) => {
    const next = [...value];
    next[index] = val;
    setValue(next);
    onChange(next.join(''));
  };

  const onValueChange = (val: string, i: number) => {
    setAt(i, val);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const paste = e.clipboardData.getData('Text').trim();
    if (!paste) return;
    const digits = paste.replace(/\D/g, '');
    if (!digits) return;
    const chars = digits.split('');
    const next = Array.from({ length }).map((_, idx) => chars[idx] || '');
    setValue(next);
    onChange(next.join(''));
    // focus the last filled or last input
    const lastIndex = Math.min(chars.length - 1, length - 1);
    const el = refs[lastIndex]?.current as any;
    if (el && typeof el.focus === 'function') el.focus();
    e.preventDefault();
  };

  return (
    <div className="verification-input">
      <div className="verification-input__container" onPaste={handlePaste}>
        {renderElements(length, {
          onChange: onValueChange,
        }, value, refs)}
      </div>
    </div>
  );
}
